import {IncidentType} from "@/lib/types/statuspages.ts";

import type {PublicStatusPage, PublicStatusPageHistoryResult} from "../types.ts";

export interface CrossTabReachability {
  canViewActiveIncidents: boolean;
  canViewActiveMaintenance: boolean;
  canViewPastIncidents: boolean;
  canViewPastMaintenance: boolean;
}

/**
 * Reachability of the cross-tab empty-state links (e.g. "View Past Incidents" on an empty Active
 * Incidents sub-tab). A target sub-tab is reachable only when its parent tab is visible AND the
 * sub-tab itself is visible. Sub-tab visibility mirrors the filters in CurrentStatusOverview /
 * HistoryOverview: shown when its toggle is on and, under hide-empty, only when it has events.
 *
 * `statusPage.show_history_tab` already reflects the "empty History tab" collapse (the caller
 * passes the effective status page). While the history query is still loading the past-event
 * links stay optimistically enabled, matching the hide-empty-after-load behavior elsewhere.
 */
export const getCrossTabReachability = (
  statusPage: PublicStatusPage,
  historyResult: PublicStatusPageHistoryResult | null
): CrossTabReachability => {
  const statusTabVisible = statusPage.show_status_tab !== false;
  const historyTabVisible = !!statusPage.show_history_tab;

  const hasActiveIncidents = statusPage.active_incidents.some((i) => i.incident_type === IncidentType.INCIDENT);
  const hasActiveMaintenance = statusPage.active_incidents.some(
    (i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE
  );

  const pastEventsLoaded = historyResult != null;
  const pastEvents = historyResult?.past_incidents ?? [];
  const hasPastIncidents = pastEvents.some((i) => i.incident_type === IncidentType.INCIDENT);
  const hasPastMaintenance = pastEvents.some((i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE);

  const activeIncidentsSubTabVisible =
    statusPage.show_active_incidents && (!statusPage.hide_empty_tabs_status || hasActiveIncidents);
  const activeMaintenanceSubTabVisible =
    statusPage.show_active_incidents && (!statusPage.hide_empty_tabs_status || hasActiveMaintenance);
  const pastIncidentsSubTabVisible =
    statusPage.show_past_incidents && (!statusPage.hide_empty_tabs_history || !pastEventsLoaded || hasPastIncidents);
  const pastMaintenanceSubTabVisible =
    statusPage.show_past_incidents && (!statusPage.hide_empty_tabs_history || !pastEventsLoaded || hasPastMaintenance);

  return {
    canViewActiveIncidents: statusTabVisible && activeIncidentsSubTabVisible,
    canViewActiveMaintenance: statusTabVisible && activeMaintenanceSubTabVisible,
    canViewPastIncidents: historyTabVisible && pastIncidentsSubTabVisible,
    canViewPastMaintenance: historyTabVisible && pastMaintenanceSubTabVisible,
  };
};
